import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from "react";
import { v7 as uuidv7 } from "uuid";

import {
  clearCsrfToken,
  fetchWithCsrfRetry,
  getCsrfToken,
  runtimeFetch,
} from "../api/client.js";
import {
  createAnalyticsClient,
  disabledAnalyticsClient,
  type AnalyticsClient,
} from "./events.js";

const AnalyticsContext = createContext<AnalyticsClient>(disabledAnalyticsClient);
const AnalyticsSessionContext = createContext<string | null>(null);

export const ANALYTICS_SESSION_STORAGE_KEY = "meta-video.analytics-session.v1";
export const ANALYTICS_VERIFICATION_RUN_STORAGE_KEY =
  "meta-video.analytics-verification-run.v1";
const INACTIVITY_LIMIT_MS = 30 * 60 * 1_000;
const ABSOLUTE_LIMIT_MS = 24 * 60 * 60 * 1_000;
const VERIFICATION_RUN_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._-]{0,127}$/;

export interface AnalyticsStorage {
  getItem(key: string): string | null;
  removeItem(key: string): unknown;
  setItem(key: string, value: string): unknown;
}

interface AnalyticsSessionRecord {
  readonly createdAt: number;
  readonly lastActivityAt: number;
  readonly sessionId: string;
  readonly userId: string;
}

export interface AnalyticsIdentity {
  readonly email: string;
  readonly id: string;
  readonly role: "viewer" | "admin" | "super_admin";
}

function browserStorage(): AnalyticsStorage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function validRecord(
  value: unknown,
  userId: string,
  now: number,
): value is AnalyticsSessionRecord {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Partial<AnalyticsSessionRecord>;
  return (
    candidate.userId === userId &&
    typeof candidate.createdAt === "number" &&
    typeof candidate.lastActivityAt === "number" &&
    typeof candidate.sessionId === "string" &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-7[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/.test(
      candidate.sessionId,
    ) &&
    candidate.createdAt <= now &&
    candidate.lastActivityAt <= now &&
    now - candidate.createdAt < ABSOLUTE_LIMIT_MS &&
    now - candidate.lastActivityAt < INACTIVITY_LIMIT_MS
  );
}

function readRecord(
  storage: AnalyticsStorage | null,
  userId: string,
  now: number,
): AnalyticsSessionRecord | null {
  const stored = storage?.getItem(ANALYTICS_SESSION_STORAGE_KEY);
  if (!stored) return null;
  try {
    const parsed: unknown = JSON.parse(stored);
    return validRecord(parsed, userId, now) ? parsed : null;
  } catch {
    return null;
  }
}

function persistRecord(
  storage: AnalyticsStorage | null,
  record: AnalyticsSessionRecord,
): void {
  try {
    storage?.setItem(ANALYTICS_SESSION_STORAGE_KEY, JSON.stringify(record));
  } catch {
    // Storage availability must not change product behavior.
  }
}

function readVerificationRunId(storage: AnalyticsStorage | null): string | null {
  try {
    const value = storage?.getItem(ANALYTICS_VERIFICATION_RUN_STORAGE_KEY);
    return value && VERIFICATION_RUN_PATTERN.test(value) ? value : null;
  } catch {
    return null;
  }
}

export function clearAnalyticsSession(
  storage: AnalyticsStorage | null = browserStorage(),
): void {
  for (const key of [
    ANALYTICS_SESSION_STORAGE_KEY,
    ANALYTICS_VERIFICATION_RUN_STORAGE_KEY,
  ]) {
    try {
      storage?.removeItem(key);
    } catch {
      // Logout and account switching continue even if storage is unavailable.
    }
  }
  clearCsrfToken();
}

export function AnalyticsProvider({
  children,
  client = disabledAnalyticsClient,
  sessionId = null,
}: PropsWithChildren<{
  readonly client?: AnalyticsClient;
  readonly sessionId?: string | null;
}>) {
  return (
    <AnalyticsSessionContext.Provider value={sessionId}>
      <AnalyticsContext.Provider value={client}>
        {children}
      </AnalyticsContext.Provider>
    </AnalyticsSessionContext.Provider>
  );
}

export function useAnalytics(): AnalyticsClient {
  return useContext(AnalyticsContext);
}

export function useAnalyticsSessionId(): string | null {
  return useContext(AnalyticsSessionContext);
}

export function AuthenticatedAnalyticsProvider({
  children,
  fetcher = runtimeFetch,
  now = Date.now,
  storage = browserStorage(),
  user,
  uuidFactory = uuidv7,
}: PropsWithChildren<{
  readonly fetcher?: typeof fetch;
  readonly now?: () => number;
  readonly storage?: AnalyticsStorage | null;
  readonly user: AnalyticsIdentity;
  readonly uuidFactory?: () => string;
}>) {
  const [active, setActive] = useState<AnalyticsSessionRecord | null>(null);
  const activeRef = useRef<AnalyticsSessionRecord | null>(null);
  activeRef.current = active;

  const createRecord = useMemo(
    () => (timestamp: number): AnalyticsSessionRecord => ({
      createdAt: timestamp,
      lastActivityAt: timestamp,
      sessionId: uuidFactory(),
      userId: user.id,
    }),
    [user.id, uuidFactory],
  );

  useEffect(() => {
    let cancelled = false;
    const timestamp = now();
    const record = readRecord(storage, user.id, timestamp) ?? createRecord(timestamp);
    persistRecord(storage, record);
    void getCsrfToken(fetcher).then(() => {
      if (!cancelled) setActive(record);
    }).catch(() => {
      if (!cancelled) setActive(null);
    });
    return () => {
      cancelled = true;
      setActive(null);
    };
  }, [createRecord, fetcher, now, storage, user.id]);

  const client = useMemo(() => {
    if (!active) return disabledAnalyticsClient;
    return createAnalyticsClient(async (request) => {
      const timestamp = now();
      let record = activeRef.current;
      if (!record || !validRecord(record, user.id, timestamp)) {
        record = createRecord(timestamp);
      } else {
        record = { ...record, lastActivityAt: timestamp };
      }
      activeRef.current = record;
      if (active.sessionId !== record.sessionId) setActive(record);
      persistRecord(storage, record);

      const verificationRunId = readVerificationRunId(storage);
      await fetchWithCsrfRetry("/api/analytics/capture", {
        body: JSON.stringify({
          ...request,
          sessionId: record.sessionId,
          ...(verificationRunId ? { verificationRunId } : {}),
        }),
        credentials: "same-origin",
        headers: {
          "content-type": "application/json",
        },
        keepalive: true,
        method: "POST",
      }, fetcher);
    });
  }, [active, createRecord, fetcher, now, storage, user.id]);

  return (
    <AnalyticsSessionContext.Provider value={active?.sessionId ?? null}>
      <AnalyticsContext.Provider value={client}>
        {children}
      </AnalyticsContext.Provider>
    </AnalyticsSessionContext.Provider>
  );
}
